import { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { scrollToSection } from "../utils/scroll";

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function BubbleCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let rafId;
    let particles = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const spawn = () => ({
      x: Math.random() * canvas.width,
      y: canvas.height + Math.random() * 200,
      r: 0.8 + Math.random() * 2.6,
      speed: 0.25 + Math.random() * 0.7,
      drift: Math.random() * Math.PI * 2,
      driftSpeed: 0.004 + Math.random() * 0.012,
      alpha: 0.08 + Math.random() * 0.3,
    });

    for (let i = 0; i < 70; i++) {
      const p = spawn();
      p.y = Math.random() * canvas.height;
      particles.push(p);
    }

    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p, idx) => {
        p.y -= p.speed;
        p.drift += p.driftSpeed;
        p.x += Math.sin(p.drift) * 0.4;
        if (p.y < -10) particles[idx] = spawn();
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 229, 255, ${p.alpha})`;
        ctx.fill();
      });
      rafId = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" aria-hidden="true" />;
}

export default function KineticHero() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 40, damping: 20 });
  const smy = useSpring(my, { stiffness: 40, damping: 20 });
  const bgX = useTransform(smx, [-0.5, 0.5], [-18, 18]);
  const bgY = useTransform(smy, [-0.5, 0.5], [-12, 12]);
  const fgX = useTransform(smx, [-0.5, 0.5], [8, -8]);

  const onMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section
      id="hero"
      data-testid="hero-landing-section"
      onMouseMove={onMouseMove}
      className="relative min-h-screen flex flex-col justify-end overflow-hidden"
    >
      <motion.div className="absolute inset-0 scale-110" style={{ x: bgX, y: bgY }}>
        <img
          src="https://images.pexels.com/photos/28915352/pexels-photo-28915352/free-photo-of-luxury-villa-with-pool-in-tropical-setting.jpeg?auto=compress&cs=tinysrgb&w=2000"
          alt="Luxuriöser Pool am Abend"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-gradient-overlay" />
      </motion.div>
      <BubbleCanvas />

      <motion.div style={{ x: fgX }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-40 pb-16 sm:pb-20">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-xs uppercase tracking-[0.32em] font-mono text-cyan-400 font-semibold mb-6 notranslate"
          translate="no"
        >
          <span className="text-white">the </span><span className="text-cyan-300">coolpool</span><span className="text-white"> company GmbH</span> · Poolbau &amp; Schwimmbadtechnik
        </motion.p>

        <h1 className="font-medium tracking-tight leading-[1.05] text-white max-w-5xl" style={{ fontSize: "clamp(2.2rem, 7.5vw, 5.5rem)" }}>
          <MaskedLine delay={0.45} className="whitespace-nowrap">WASSER &amp;</MaskedLine>
          <MaskedLine delay={0.58} className="whitespace-nowrap">TECHNIK —</MaskedLine>
          <MaskedLine delay={0.71} className="whitespace-nowrap italic text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-cyan-400 to-teal-300">
            in perfekter
          </MaskedLine>
          <MaskedLine delay={0.84} className="whitespace-nowrap">FORM.</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-7 max-w-md text-base sm:text-lg text-slate-300 leading-relaxed font-light"
        >
          Exklusiver Poolbau und intelligente Schwimmbadtechnik —
          individuell geplant, in ganz Deutschland.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-configure-pool-button"
            onClick={() => window.open("https://konfigurator.cool-pool-company.de/", "_blank", "noopener")}
            className="cyan-glow-btn text-[#050B14] font-semibold px-7 py-4 rounded-full inline-flex items-center gap-2 text-sm sm:text-base"
          >
            Pool konfigurieren <ArrowRight className="w-4 h-4" />
          </button>
          <button
            data-testid="hero-discover-technik-button"
            onClick={() => scrollToSection("#technik")}
            className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/20 text-sm sm:text-base text-slate-100 hover:border-cyan-400/70 hover:bg-cyan-400/10 hover:text-cyan-200 transition-all duration-300"
          >
            Mehr erfahren
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-down-indicator"
        onClick={() => scrollToSection("#technik")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 8, 0] }}
        transition={{ opacity: { delay: 2.2, duration: 1 }, y: { repeat: Infinity, duration: 2, ease: "easeInOut" } }}
        className="absolute bottom-6 right-6 sm:right-10 z-10 text-cyan-300/70 hover:text-cyan-300 transition-colors"
        aria-label="Nach unten scrollen"
      >
        <ChevronDown className="w-7 h-7" />
      </motion.button>
    </section>
  );
}
